import PropTypes from 'prop-types';
import {
  InputCheckboxComponent,
  ImageUploadComponent,
  InputComponent,
  InputSelectComponent,
  AddButtonComponent,
  RadioButtonComponent,
  DateComponent,
  TextAreaComponent,
  DocumentUploadComponent,
  CustomerAutosuggest,
  VehicleAutosuggest,
  ItemAutosuggest,
  QuotationBodyAutosuggest,
} from '../index';

import {
  updateNestedState,
} from '../../../utils';

// Configuration for select fields
const SELECT_FIELD_CONFIG = {
  'customer_type_id': { options: 'customerTypeList', handler: 'onCustomerType', navigate: '/CustomerType' },
  'department_id': { options: 'departmentList', handler: 'onSelectDepartment', navigate: '/StaffDepartment' },
  'designation_id': { options: 'designationList', handler: 'onSelectDesignation', navigate: '/StaffDesignation' },
  'category_id': { options: 'categoryList', handler: 'onSelectCategory', navigate: '/StaffCategory' },
  'blood_group_id': { options: 'bloodGroupList', handler: 'onSelectBloodGroup', navigate: '/StaffCategory' },
  'item_type_id': { options: 'itemTypeList', handler: 'onItemType', navigate: '/ItemType' },
  'item_category_id': { options: 'itemCategoryList', handler: 'onItemCategory', navigate: '/ItemCategory' },
  'item_sub_category_id': { options: 'itemSubCategoryList', handler: 'onItemSubCategory', navigate: '/ItemSubCategory' },
  'brand_id': { options: 'brandList', handler: 'onBrand', navigate: '/BrandCategory' },
  'model_id': { options: 'modelList', handler: 'onModel', navigate: '/ModelCategory' },
  'rack_id': { options: 'rackList', handler: 'onRack', navigate: '/RackCategory' },
  'generic_id': { options: 'genericList', handler: 'onGeneric', navigate: '/GenericType' },
  'schedule_id': { options: 'scheduleList', handler: 'onSchedule', navigate: '/ScheduleCategory' },
  'tax_setting_id': { options: 'taxSettingList', handler: 'onTaxSetting', navigate: '/TaxSetting' },
  'reference_id': { options: 'referenceList', handler: 'onReference', navigate: '/EnquiryReference' },
  'status_type_id': { options: 'statusSelectBoxList', handler: 'onStatusType', navigate: '/StatusType' },
  'service_ids': { options: 'serviceList', handler: 'onServiceIds', navigate: '/EnquiryService' },
  'staff_ids': { options: 'staffList', handler: 'onStaffIds', navigate: '/TaxSetting' },
  'incharge_ids': { options: 'inchargeList', handler: 'onInchargeIds', navigate: '/TaxSetting' },
  'condition_id': { options: 'conditionList', handler: 'onConditionId', navigate: '/QuotationCondition' },
  'feature_id': { options: 'featureList', handler: 'onFeatureId', navigate: '/QuotationFeatures' },
  'covered_under_amc_id': { options: 'coveredUnderAMCList', handler: 'onCoveredUnderAMCId', navigate: '/CoveredUnderAMC' },
  'not_covered_under_amc_id': { options: 'notCoveredUnderAMCList', handler: 'onNotCoveredUnderAMCId', navigate: '/NotCoveredUnderAMC' },
  'expense_type_id': { options: 'expenseTypeList', handler: 'onExpenseType', navigate: '/ExpenseType' },
  'define_expense_id': { options: 'defineExpenseList', handler: 'onDefineExpense', navigate: '/DefineExpense' },
  'mode_of_pay': { options: 'modeOfPayList', handler: 'onModeOfPay', navigate: '/DefineExpense' },
  'bank_id': { options: 'bankSelectBoxList', handler: 'onBank', navigate: '/AddBank' },
  'invoice_nos': { options: 'invoiceNosList', handler: 'onInvoiceNos', navigate: '/EnquiryService' },
  'sales_man_id': { options: 'staffSelectBoxList', handler: 'onSaleManId', navigate: '/TaxSetting' },
};

const ADDRESS_FIELDS = {
  'address.country': 'country_list',
  'address.state': 'state_list',
  'address.city': 'city_list',
};

const STAFF_ADDRESS_FIELDS = {
  'staff.address.country': 'country_list',
  'staff.address.state': 'state_list',
  'staff.address.city': 'city_list',
};

const CUSTOMER_ADDRESS_FIELDS = {
  'customer.address.country': 'country_list',
  'customer.address.state': 'state_list',
  'customer.address.city': 'city_list',
};

const DynamicFormField = ({
  field,
  formData,
  setFormData,
  handleChange,
  handleDateChange,
  handleFileChange,
  dropdownData,
  handlers,
  profileImages,
  files,
  setFiles,
  setDeletedFileIds,
  navigate,
  ...lists // Rest parameter for all list props
}) => {
  // Utility functions
  const getValueFromNestedPath = (obj, path) =>
    path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : ''), obj);

  const getDateValueFromNestedPath = (obj, path) => {
    const value = path.split('.').reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : null), obj);
    return value ? new Date(value) : null;
  };

  // Common props for input components
  const commonProps = {
    ...field,
    value: getValueFromNestedPath(formData, field.name),
    onChange: handleChange,
  };

  // Render select field with add button
  const renderSelectWithAdd = (config) => (
    <div className="flex flex-col sm:flex-row items-center w-full gap-2">
      <InputSelectComponent
        {...field}
        value={commonProps.value}
        options={lists[config.options]}
        onSelect={handlers[config.handler]}
      />
      {field.hasAddButton && <AddButtonComponent onClick={() => navigate(config.navigate)} />}
    </div>
  );

  // Render address select field
  const renderAddressSelect = (fieldName, optionsKey, handlerKey) => (
    <InputSelectComponent
      {...field}
      value={commonProps.value}
      options={dropdownData[optionsKey]}
      onSelect={handlers[handlerKey]}
    />
  );

  // Main render logic
  switch (field.type) {
    case 'select': {
      // Handle configured select fields
      if (SELECT_FIELD_CONFIG[field.field_name]) {
        return renderSelectWithAdd(SELECT_FIELD_CONFIG[field.field_name]);
      }

      // Handle address fields
      if (field.name.startsWith('address.')) {
        const optionsKey = ADDRESS_FIELDS[field.name];
        if (optionsKey) {
          return renderAddressSelect(field.name, optionsKey, `onSelect${optionsKey.split('_')[0].capitalize()}`);
        }
      }

      // Handle staff address fields
      if (field.name.startsWith('staff.address.')) {
        const optionsKey = STAFF_ADDRESS_FIELDS[field.name];
        if (optionsKey) {
          return renderAddressSelect(field.name, optionsKey, `onSelect${optionsKey.split('_')[0].capitalize()}`);
        }
      }

      // Handle customer address fields
      if (field.name.startsWith('customer.address.')) {
        const optionsKey = CUSTOMER_ADDRESS_FIELDS[field.name];
        if (optionsKey) {
          return renderAddressSelect(field.name, optionsKey, `onSelect${optionsKey.split('_')[0].capitalize()}`);
        }
      }
      break;
    }

    case 'Autosuggest': {
      if (field.field_name === "customer_name" || field.field_name === "dealer_name" || field.field_name === "merchant_name" || field.field_name === "distributor_name") {
        return (
          <CustomerAutosuggest
            {...commonProps}
            setFormData={setFormData}
          />
        );
      } else if (field.field_name === "vehicle_nick_name") {
        return (
          <VehicleAutosuggest
            {...commonProps}
            setFormData={setFormData}
          />
        );
      } else if (field.field_name === "item_name") {
        return (
          <ItemAutosuggest
            {...commonProps}
            setFormData={setFormData}
          />
        );
      } else if (field.field_name === "subject") {
        return (
          <QuotationBodyAutosuggest
            {...commonProps}
            setFormData={setFormData}
          />
        );
      }
      break;
    }

    case 'image':
      return (
        <ImageUploadComponent
          {...field}
          image_src={profileImages[field.name + '_prev']}
          onFileChangeHandler={(e) => handleFileChange(e, field.name, field.name + '_prev')}
        />
      );

    case 'checkbox':
      return <InputCheckboxComponent {...commonProps}
        type="checkbox"
        checked={getDateValueFromNestedPath(formData, field.name)}
        onChange={(value) =>
          setFormData((prev) =>
            updateNestedState(prev, field.name, !value)
          )
        }
      />;

    case 'radio':
      return <RadioButtonComponent {...commonProps} type="radio" />;

    case 'textarea':
      return <TextAreaComponent {...commonProps} type="textarea" />;

    case 'date':
      return (
        <DateComponent
          {...field}
          type="date"
          value={getDateValueFromNestedPath(formData, field.name)}
          onChange={(date) =>
            setFormData((prev) =>
              updateNestedState(prev, field.name, date)
            )
          }
        />
      );

    case 'multiFiles':
      return (
        <DocumentUploadComponent
          {...field}
          type="multiFiles"
          value={commonProps.value}
          files={files}
          setFiles={setFiles}
          setDeletedFileIds={setDeletedFileIds}
        />
      );

    default:
      if (!field.type) {
        console.error('Field type is missing:', field);
        return null;
      }
      return <InputComponent {...commonProps} type={field.type} />;
  }

  return null; // Fallback for unhandled cases
};

// Helper method to capitalize strings
String.prototype.capitalize = function () {
  return this.charAt(0).toUpperCase() + this.slice(1);
};

DynamicFormField.propTypes = {
  field: PropTypes.object.isRequired,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func,
  handleChange: PropTypes.func,
  handleFileChange: PropTypes.func,
  handleDateChange: PropTypes.func,
  dropdownData: PropTypes.object,
  handlers: PropTypes.object,
  profileImages: PropTypes.object,
  navigate: PropTypes.func,
  files: PropTypes.array,
  setFiles: PropTypes.func,
  setDeletedFileIds: PropTypes.func,
  customerTypeList: PropTypes.array,
  departmentList: PropTypes.array,
  designationList: PropTypes.array,
  categoryList: PropTypes.array,
  bloodGroupList: PropTypes.array,
  itemTypeList: PropTypes.array,
  itemCategoryList: PropTypes.array,
  itemSubCategoryList: PropTypes.array,
  brandList: PropTypes.array,
  modelList: PropTypes.array,
  rackList: PropTypes.array,
  genericList: PropTypes.array,
  scheduleList: PropTypes.array,
  taxSettingList: PropTypes.array,
};

export default DynamicFormField;